import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import "@/i18n";
import global, { createAppResourceService } from "@/global";
import { useActAs } from "@/contexts/ActAsContext";
import type { ClinicianIdRecord } from "@/pages/settings/components/schemas";
import { createSensitiveActionAuthorizationController } from "./sensitiveActionAuthorizationController";

const CLINICIAN_IDS_ID = "trier";
// @ts-ignore
const cliniciansService = createAppResourceService<{ clinicianIds: ClinicianIdRecord[] }>("meta/clinicians");

function getDisplayName(record: ClinicianIdRecord): string {
  if (record.firstName || record.lastName) {
    return [record.firstName, record.lastName].filter(Boolean).join(" ").trim();
  }
  return record.clinicianId ?? record.id ?? "Unknown";
}

function getOptionLabel(record: ClinicianIdRecord): string {
  const name = getDisplayName(record);
  const ref = record.reference?.trim();
  if (!ref) return name;
  return `${name} – ${ref}`;
}

export type SensitiveActionAuthorizationDetails = {
  token: string;
  clinicianId: string;
  displayName: string;
};

export type SensitiveActionAuthorizationRequest = {
  reason: string;
  title?: string;
  message?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
};

type NormalizedSensitiveActionAuthorizationRequest = {
  reason: string;
  title: string;
  message: React.ReactNode;
  confirmLabel: string;
  cancelLabel: string;
};

type SensitiveActionAuthorizationDialogProps = {
  open: boolean;
  request: SensitiveActionAuthorizationRequest | null;
  onCancel: () => void;
  onAuthorized: (details: SensitiveActionAuthorizationDetails) => void;
};

const hasDisplayContent = (value: React.ReactNode): boolean => {
  if (typeof value === "string") return value.trim().length > 0;
  return value !== null && value !== undefined && value !== false;
};

const textOrFallback = (value: string, fallback: string): string => {
  return value.trim() || fallback;
};

function normalizeAuthorizationRequest(
  request: SensitiveActionAuthorizationRequest | null,
  defaults: NormalizedSensitiveActionAuthorizationRequest,
): NormalizedSensitiveActionAuthorizationRequest {
  return {
    reason: request?.reason || defaults.reason,
    title: request?.title?.trim() || defaults.title,
    message: hasDisplayContent(request?.message) ? request?.message : defaults.message,
    confirmLabel: request?.confirmLabel?.trim() || defaults.confirmLabel,
    cancelLabel: request?.cancelLabel?.trim() || defaults.cancelLabel,
  };
}

export const SensitiveActionAuthorizationDialog: React.FC<SensitiveActionAuthorizationDialogProps> = ({
  open,
  request,
  onCancel,
  onAuthorized,
}) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const { actAsState } = useActAs();
  const currentUserOid = String(auth.user?.profile?.oid ?? "");
  const clinicianInputRef = useRef<HTMLInputElement>(null);
  const operatorCodeRef = useRef<HTMLInputElement>(null);
  const [selected, setSelected] = useState<ClinicianIdRecord | null>(null);
  const [operatorCode, setOperatorCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const authorizationRequest = normalizeAuthorizationRequest(request, {
    reason: "sensitive-action",
    title: textOrFallback(t("sensitiveActionAuthorization.title"), "Security confirmation required"),
    message: textOrFallback(t("sensitiveActionAuthorization.message"), "Confirm clinician and operator code to continue."),
    confirmLabel: textOrFallback(t("common.buttons.confirm"), "Confirm"),
    cancelLabel: textOrFallback(t("common.buttons.cancel"), "Cancel"),
  });

  const { data, isLoading } = cliniciansService.useGetOne(CLINICIAN_IDS_ID, {
    refetchOnWindowFocus: false,
    enabled: open,
  });

  const clinicianList = useMemo(() => {
    const ids = Array.isArray(data?.clinicianIds) ? data.clinicianIds : [];
    return ids;
  }, [data?.clinicianIds]);

  useEffect(() => {
    if (!open) return;
    setOperatorCode("");
    setError(null);
    if (isLoading) {
      setSelected(null);
      return;
    }

    const recordId = actAsState.actAsRecordId;
    const defaultClinician =
      actAsState.isActingAs
        ? (recordId ? clinicianList.find((r) => r.id === recordId) ?? null : null)
        : clinicianList.find((r) => r.identityId === currentUserOid) ?? null;
    setSelected(defaultClinician);
    const focusTargetRef = defaultClinician ? operatorCodeRef : clinicianInputRef;
    const animationFrame = requestAnimationFrame(() => {
      focusTargetRef.current?.focus();
    });

    return () => {
      cancelAnimationFrame(animationFrame);
    };
  }, [open, isLoading, clinicianList, currentUserOid, actAsState.isActingAs, actAsState.actAsRecordId]);

  const handleSubmit = async () => {
    if (!selected?.clinicianId || !selected?.id) {
      setError(t("sensitiveActionAuthorization.errors.selectClinician"));
      return;
    }
    if (!operatorCode.trim()) {
      setError(t("sensitiveActionAuthorization.errors.operatorCodeRequired"));
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      const res = await global.client.post("meta/custom/initiate-act-as", {
        actAsClinicianId: selected.clinicianId,
        actAsRecordId: selected.id,
        operatorCode: operatorCode.trim(),
      });
      const token = (res as { data?: { token?: string } })?.data?.token;
      if (!token) {
        setError(t("sensitiveActionAuthorization.errors.invalidResponse"));
        return;
      }
      onAuthorized({
        token,
        clinicianId: selected.clinicianId,
        displayName: getDisplayName(selected),
      });
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
        ?? (err as Error)?.message
        ?? t("sensitiveActionAuthorization.errors.verificationFailed");
      setError(message);
      requestAnimationFrame(() => {
        const input = operatorCodeRef.current;
        if (input) {
          input.focus();
          input.select();
        }
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{authorizationRequest.title}</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          {authorizationRequest.message}
        </Alert>
        {isLoading ? (
          <Typography variant="body2">{t("sensitiveActionAuthorization.loadingClinicians")}</Typography>
        ) : (
          <Autocomplete
            value={selected}
            onChange={(_, newValue) => {
              setSelected(newValue);
              setError(null);
            }}
            options={clinicianList}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            fullWidth
            autoHighlight
            openOnFocus
            sx={{
              "& .MuiInputBase-root": {
                height: "38.99px",
                padding: "0 9px",
              },
              "& .MuiInputBase-input": {
                padding: "8.5px 4px !important",
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={clinicianInputRef}
                label={t("sensitiveActionAuthorization.fields.clinician.label")}
                placeholder={t("sensitiveActionAuthorization.fields.clinician.placeholder")}
                variant="outlined"
              />
            )}
          />
        )}
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            inputRef={operatorCodeRef}
            label={t("sensitiveActionAuthorization.fields.operatorCode.label")}
            type="password"
            autoComplete="current-password"
            value={operatorCode}
            onChange={(e) => {
              setOperatorCode(e.target.value);
              setError(null);
            }}
            error={!!error}
            helperText={error}
            disabled={submitting}
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{authorizationRequest.cancelLabel}</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting || isLoading}>
          {submitting ? t("sensitiveActionAuthorization.verifying") : authorizationRequest.confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export function useSensitiveActionAuthorization() {
  const [pendingRequest, setPendingRequest] = useState<SensitiveActionAuthorizationRequest | null>(null);
  const controllerRef = useRef<ReturnType<typeof createSensitiveActionAuthorizationController> | null>(null);
  if (!controllerRef.current) {
    controllerRef.current = createSensitiveActionAuthorizationController(setPendingRequest);
  }
  const controller = controllerRef.current;

  useEffect(() => {
    return () => {
      controller.cancelPendingAuthorization();
    };
  }, [controller]);

  const AuthorizationDialog = React.useCallback(() => (
    <SensitiveActionAuthorizationDialog
      open={!!pendingRequest}
      request={pendingRequest}
      onCancel={controller.cancelPendingAuthorization}
      onAuthorized={controller.settleAuthorization}
    />
  ), [controller, pendingRequest]);

  return {
    requestAuthorization: controller.requestAuthorization,
    AuthorizationDialog,
    isAuthorizationPending: !!pendingRequest,
  };
}
